import {
  Button,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui';
import LPStats from '@/features/lp/components/lp-stats';
import PendingWithdrawalsTable from '@/features/lp/components/pending-withdrawals-table';
import { LPTransferDialog } from '@/features/lp/components/lp-transfer-dialog';
import PerformanceChart from '@/features/lp/components/performance-chart';
import {
  ExchangeInfo,
  useExchangeInfo,
} from '@/features/markets/api/get-exchange-info';
import { cn } from '@/lib/utils';
import { useAccountStore } from '@/store/use-account-store';
import { LPOrderType } from '@/types';
import {
  formatBigInt,
  formatNumber,
  parseDecimalToBigInt,
} from '@/utils/value-format';
import useLpPerformance, { PERIODS } from '@/features/lp/api/get-performance';
import { useMemo } from 'react';
import HistoricalTransfersTable from '@/features/lp/components/historical-transfers-table';
import TooltipWrapper from '@/components/ui/tooltip';
import { useFetchAccount } from '@/features/account/api/get-account';
import { COLLATERAL_DECIMALS } from '@/store/use-markets-store';

export default function LPPage() {
  const { hasAuth } = useAccountStore();

  const { data } = useExchangeInfo();
  const exchangeInfo = data as ExchangeInfo;
  const { data: lpPerformance } = useLpPerformance(PERIODS[PERIODS.length - 1]);

  const totalApy = useMemo(() => {
    if (!Array.isArray(lpPerformance) || lpPerformance.length === 0) return 0;
    const sortedLpPerformance = lpPerformance.sort(
      (a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime(),
    );
    // const numberOfDays = PERIODS[1].offset / (24 * 60 * 60 * 1000);
    const numberOfDays =
      (new Date(
        sortedLpPerformance[sortedLpPerformance.length - 1].ts,
      ).getTime() -
        new Date(sortedLpPerformance[0].ts).getTime()) /
      (24 * 60 * 60 * 1000);
    const performanceApy =
      (parseFloat(
        sortedLpPerformance[sortedLpPerformance.length - 1].pricePerShare,
      ) /
        parseFloat(sortedLpPerformance[0].pricePerShare) -
        1) *
      (365 / numberOfDays) *
      100;
    return Math.round(performanceApy);
  }, [lpPerformance]);

  const { data: account, isPending } = useFetchAccount();

  const balance = account
    ? parseDecimalToBigInt(account.lp.balance, COLLATERAL_DECIMALS)
    : 0n;

  const unrealizedPnl = account
    ? parseDecimalToBigInt(account.lp.unrealizedPnl, COLLATERAL_DECIMALS)
    : 0n;

  return (
    <div className="flex h-full flex-col">
      <div className="flex grid-cols-5 items-center border-border bg-background px-6 pb-4 pt-5 md:border-b md:px-8 md:py-10 xl:grid">
        <div className="flex justify-between max-md:w-full md:flex-col md:gap-8">
          <div className="flex flex-col gap-2 md:gap-8">
            <h1 className="uppercase text-vestgrey-600 max-md:font-mono md:text-3xl md:font-semibold md:normal-case md:text-foreground lg:text-4xl">
              <span className="md:hidden">Current Balance</span>
              <span className="hidden md:inline">Liquidity Providing</span>
            </h1>
            <span className="font-mono text-4xl font-medium tracking-tighter text-foreground md:hidden">
              {formatNumber(account?.lp.balance, { digits: 2 })}
            </span>
            <span
              className={cn(
                'font-mono text-lg font-medium md:hidden',
                isPending && 'opacity-0',
                unrealizedPnl > 0n ? 'text-green' : 'text-red',
              )}
            >
              {formatNumber(account?.lp.unrealizedPnl, {
                digits: 2,
                showChange: true,
              })}{' '}
              {`(${formatBigInt(balance > 0n ? (unrealizedPnl * 10000n) / balance : 0n, 2, { digits: 2, style: 'percent', showChange: false })})`}
            </span>
          </div>
          <dl className="grid gap-2 md:grid-cols-2 md:gap-6">
            <div className="flex flex-col gap-1">
              <TooltipWrapper
                align="left"
                content="Calculated based on past 90-day performance, annualized. Actual returns will vary based on fees collected and PnL."
                className="hidden md:block"
              >
                <dt className="select-none font-mono text-sm uppercase tracking-wide text-vestgrey-600 md:underline">
                  Expected APY
                </dt>
              </TooltipWrapper>
              <dd className="text-font text-2xl font-bold">
                <div className="flex items-center gap-2">
                  <span className="text-lg md:text-xl lg:text-2xl">
                    {totalApy}%
                  </span>
                </div>
              </dd>
            </div>
            <div className="flex flex-col gap-1">
              <dt className="font-mono text-sm uppercase tracking-wide text-vestgrey-600">
                TVL
              </dt>
              <dd
                className={cn(
                  'text-font text-lg font-bold md:text-xl lg:text-2xl',
                )}
              >
                {formatNumber(exchangeInfo.exchange.lp, {
                  digits: 2,
                  compact: true,
                  round: 'ceil',
                })}{' '}
                / 10M
              </dd>
            </div>
            {/* <div className="flex flex-col gap-2">
              <dt className="font-mono text-sm uppercase tracking-wider text-vestgrey-600">
                Max drawdown
              </dt>
              <dd className={cn('text-font text-xl font-bold')}>-</dd>
            </div> */}
          </dl>
        </div>
        <div className="col-span-3 hidden flex-1 px-8 md:block">
          <p className="max-w-7xl text-vestgrey-100">
            This vault provides liquidity by acting as the counterparty to
            trades. While temporary drawdowns can occur, it accrues fees and
            charges risk-based premia and funding to support long-term
            performance. The lock up period is 8 hours. Learn more{' '}
            <a
              href="https://docs.vest.exchange/overview/liquidity-providers"
              className="font-medium underline"
              target="_blank"
            >
              here
            </a>
            .
          </p>
        </div>
        <div className="hidden pr-8 md:block">
          <LPTransferDialog orderType={LPOrderType.DEPOSIT}>
            <Button
              disabled={!hasAuth}
              size="lg"
              className="h-auto rounded-none px-12 py-6 font-mono uppercase tracking-wider"
            >
              Provide Liquidity
            </Button>
          </LPTransferDialog>
        </div>
      </div>
      <div className="flex grid-cols-4 grid-rows-[max-content_1fr] flex-col xl:grid">
        <LPStats />
        <PerformanceChart />
      </div>
      <Tabs className="flex w-full flex-1 flex-col" defaultValue="pending">
        <div className="flex w-full">
          <TabsList className="grid w-full grid-cols-2 md:flex md:w-auto">
            <TabsTrigger className="h-14" value="pending">
              PENDING WITHDRAWS
            </TabsTrigger>
            <TabsTrigger className="h-14" value="historical">
              HISTORICAL TRANSFERS
            </TabsTrigger>
          </TabsList>
          <div className="hidden flex-1 border-y border-border bg-background md:block"></div>
        </div>
        <TabsContent className="relative flex-1" value="pending">
          <PendingWithdrawalsTable />
        </TabsContent>
        <TabsContent className="relative flex-1" value="historical">
          <HistoricalTransfersTable />
        </TabsContent>
      </Tabs>
      <div className="grid grid-cols-2 gap-3 p-6 md:hidden">
        <LPTransferDialog orderType={LPOrderType.DEPOSIT}>
          <Button
            disabled={!hasAuth}
            size="xl"
            className="h-14 font-mono text-sm font-normal uppercase tracking-wider"
          >
            Provide Liquidity
          </Button>
        </LPTransferDialog>
        <LPTransferDialog orderType={LPOrderType.SCHEDULE_WITHDRAW}>
          <Button
            disabled={!hasAuth}
            size="xl"
            variant="secondary"
            className="h-14 font-mono text-sm font-normal uppercase tracking-wider"
          >
            WITHDRAW LIQUIDITY
          </Button>
        </LPTransferDialog>
      </div>
    </div>
  );
}
